import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { Coffee, Sun } from "lucide-react";
import { FocusTimer } from "@/components/FocusTimer";
import { BreathingOrb } from "@/components/BreathingOrb";
import { useBusiness } from "@/components/business-context";
import { getToday } from "@/lib/momentum.functions";
import { todayISO } from "@/lib/momentum-types";
import { pickQuote } from "@/lib/quotes";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/focus")({
  head: () => ({
    meta: [
      { title: "Focus — The Founders App" },
      {
        name: "description",
        content: "A quiet timer page for working one move at a time, block by block.",
      },
      { property: "og:title", content: "Focus — The Founders App" },
      {
        property: "og:description",
        content: "Run a focus block against one move and log the session.",
      },
    ],
  }),
  component: FocusPage,
});

const LOG_KEY = "founders.focus-log.v1";

interface Block {
  /** Epoch ms the block finished. */
  at: number;
  minutes: number;
  label: string;
}

function readLog(): Block[] {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    const parsed = raw ? (JSON.parse(raw) as Block[]) : [];
    return Array.isArray(parsed) ? parsed.slice(0, 60) : [];
  } catch {
    return [];
  }
}

function sameLocalDay(ms: number): boolean {
  const d = new Date(ms);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function timeOf(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function FocusPage() {
  const { active } = useBusiness();
  const businessId = active?.id ?? "";
  const date = todayISO();

  const fetchToday = useServerFn(getToday);
  const todayQuery = useQuery({
    queryKey: ["today", businessId, date],
    enabled: Boolean(businessId),
    queryFn: () => fetchToday({ data: { businessId, date } }),
  });

  const moves = (todayQuery.data?.entries ?? []) as {
    id: string;
    morning_text: string;
    done: boolean | null;
  }[];
  const openMoves = moves.filter((move) => move.done !== true);

  const [focusId, setFocusId] = useState<string | null>(null);
  const [log, setLog] = useState<Block[]>([]);
  const [quote] = useState(() => pickQuote());
  const [justFinished, setJustFinished] = useState(false);

  useEffect(() => {
    setLog(readLog());
  }, []);

  // Keep focus attached only to moves that still need an outcome.
  useEffect(() => {
    if (openMoves.length === 0) {
      if (focusId) setFocusId(null);
      return;
    }
    if (!focusId || !openMoves.some((move) => move.id === focusId)) {
      const firstOpenMove = openMoves[0];
      if (firstOpenMove) setFocusId(firstOpenMove.id);
    }
  }, [openMoves, focusId]);

  const focused = openMoves.find((move) => move.id === focusId) ?? null;

  const onComplete = useCallback(
    (minutes: number) => {
      const entry: Block = {
        at: Date.now(),
        minutes,
        label: focused?.morning_text ?? "Open focus block",
      };
      setLog((prev) => {
        const next = [entry, ...prev].slice(0, 60);
        try {
          localStorage.setItem(LOG_KEY, JSON.stringify(next));
        } catch {
          /* storage unavailable — the log just won't persist */
        }
        return next;
      });
      setJustFinished(true);
    },
    [focused],
  );

  const clearLog = () => {
    setLog([]);
    try {
      localStorage.removeItem(LOG_KEY);
    } catch {
      /* nothing to do */
    }
  };

  const todayBlocks = log.filter((b) => sameLocalDay(b.at));
  const todayMinutes = todayBlocks.reduce((sum, b) => sum + b.minutes, 0);

  if (!businessId) return null;

  return (
    <div className="space-y-8">
      <section className="vault-panel p-7">
        <p className="micro-label">Focus</p>
        <h2 className="mt-3 text-xl font-medium">One block at a time</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick what you're working on, set a length, and let the rest of the app wait. The timer
          keeps running if you wander off to another page.
        </p>
      </section>

      <section className="vault-panel px-5 py-5 sm:px-6">
        <p className="micro-label">Working on</p>
        {openMoves.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {moves.length === 0
              ? "Nothing set for today yet. "
              : "All of today's moves have outcomes saved. "}
            <Link to="/app/today" className="text-foreground underline-offset-4 hover:underline">
              {moves.length === 0 ? "Write a move" : "View today"}
            </Link>{" "}
            {moves.length === 0
              ? "first, or just run an open block."
              : "or run an open focus block."}
          </p>
        ) : (
          <div className="mt-3 space-y-px">
            {openMoves.map((m, i) => (
              <button
                key={m.id}
                onClick={() => setFocusId(m.id)}
                className={cn(
                  "flex w-full items-baseline gap-3 rounded-sm px-2 py-2.5 text-left text-sm transition-colors",
                  focusId === m.id
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                )}
              >
                <span className="text-[0.625rem] tabular-nums text-muted-foreground/50">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="notranslate flex-1 truncate">{m.morning_text}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <FocusTimer
          label={focused?.morning_text ?? undefined}
          presets={[15, 25, 50, 90]}
          size="lg"
          customizable
          onComplete={onComplete}
        />
        <BreathingOrb />
      </div>

      {justFinished && (
        <section className="vault-panel px-5 py-5 sm:px-6">
          <span className="micro-label inline-flex items-center gap-2">
            <Coffee className="h-3.5 w-3.5" aria-hidden="true" /> Block done
          </span>
          <p className="mt-3 text-sm text-muted-foreground">
            Stand up for a few minutes. When you're back, either run another block or log what
            happened while it's still fresh.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button asChild size="sm" variant="secondary">
              <Link to="/app/today">
                <Sun className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Log the outcome
              </Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setJustFinished(false)}>
              Keep going
            </Button>
          </div>
        </section>
      )}

      <section className="vault-panel px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="micro-label">Sessions today</p>
          <p className="text-xs text-muted-foreground tabular-nums">
            {todayBlocks.length} block{todayBlocks.length === 1 ? "" : "s"} · {todayMinutes} min
          </p>
        </div>
        {log.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Finished blocks show up here. It stays on this device only.
          </p>
        ) : (
          <>
            <ul className="mt-4 space-y-2">
              {log.slice(0, 12).map((b) => (
                <li
                  key={`${b.at}`}
                  className="flex items-baseline gap-3 border-b border-border pb-2 text-sm last:border-0"
                >
                  <span className="text-xs tabular-nums text-muted-foreground">{timeOf(b.at)}</span>
                  <span className="notranslate flex-1 truncate text-muted-foreground">
                    {b.label}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">{b.minutes}m</span>
                </li>
              ))}
            </ul>
            <button
              onClick={clearLog}
              className="mt-4 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Clear session log
            </button>
          </>
        )}
      </section>

      <p className="text-sm italic leading-relaxed text-muted-foreground">{quote.text}</p>
    </div>
  );
}
